/*
 #*===================================================================
 #*
 #* Licensed Materials - Property of GameTimeFootage Inc.
 #*
 #* Copyright NextPro 2019. All Rights Reserved.
 #*
 #*===================================================================
 */

import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { filter, takeUntil, tap } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { HttpEventType } from '@angular/common/http';
import dayjs from 'dayjs';

import { AssetsService, FormHelpersService } from 'app/shared/services';
import { environment } from 'environments/environment';
import { CustomTypeAheadComponent } from 'app/shared/components/custom-type-ahead/custom-type-ahead.component';
import { Constants } from 'app/shared/models/constants.model';
import * as fromStore from 'app/library/store';

@Component({
  selector: 'app-highlight-reel-play-select-modal',
  templateUrl: './highlight-reel-play-select-modal.component.html',
  styleUrls: ['./highlight-reel-play-select-modal.component.scss'],
})
export class HighlightReelPlaySelectModalComponent implements OnInit {
  @Input() userEvents: any = [];
  @Input() playInfo: any = {};
  @Input() readonly: boolean;
  @Input() purchaseId: number;
  @Input() maxPlays: number;

  @Output() closeModal: EventEmitter<any> = new EventEmitter();

  @ViewChild('gameAutocomplete') gameAutocomplete: CustomTypeAheadComponent;
  @ViewChild('eventAutocomplete') eventAutocomplete: CustomTypeAheadComponent;

  eventAutocompleteOptions: any;
  gameAutocompletOptions: any;
  selectedTeams: any[];
  eventId;
  gameId;
  playInfoForm: FormGroup;
  showForm = false;
  viewType: string;
  userClips;
  uploadingVideo = false;
  uploadProgress = 0;
  fileLoaded = false;
  fileDrag = false;
  file: File;

  readonly HR_TYPES = Constants.HIGHLIGHT_REEL_TYPES;
  readonly MAX_PLAYS = Constants.MAX_REEL_PLAYS.PRO;
  readonly MODAL_TYPES = Constants.HIGHLIGHT_REEL_SELECT_PLAY_TYPES;

  private componentDestroyed: Subject<any> = new Subject();

  constructor(
    private store: Store<fromStore.LibraryState>,
    public formHelpersService: FormHelpersService,
    private assetsService: AssetsService
  ) {}

  ngOnInit() {
    this.playInfoForm = new FormGroup({
      id: new FormControl(this.playInfo.id),
      index: new FormControl(this.playInfo.index),
      eventId: new FormControl(this.playInfo.eventId, [Validators.required]),
      eventName: new FormControl(this.playInfo.eventName),
      gameId: new FormControl(this.playInfo.gameId, [Validators.required]),
      gameName: new FormControl(this.playInfo.gameName),
      clip: new FormControl(this.playInfo.clip),
      clipId: new FormControl(this.playInfo.clipId),
      clipName: new FormControl(this.playInfo.clipName),
      clipEvent: new FormControl(this.playInfo.clipEvent),
      clipGame: new FormControl(this.playInfo.clipGame),
      title: new FormControl(this.playInfo.title),
      hrDate: new FormControl(dayjs(this.playInfo.hrDate).format('MM/DD/YY')),
      timecode: new FormControl(this.playInfo.timecode),
      jerseyNumber: new FormControl(this.playInfo.jerseyNumber, [Validators.required]),
      description: new FormControl(this.playInfo.description),
      thumbnail: new FormControl(this.playInfo.thumbnail),
      videoUrl: new FormControl(this.playInfo.videoUrl),
      type: new FormControl(this.playInfo.type),
      clipOption: new FormControl(),
    });

    if (this.playInfo.clipId) {
      this.selectType(null, this.MODAL_TYPES.SELECT_CLIP);
    } else if (this.playInfo.videoUrl) {
      this.selectType(null, this.MODAL_TYPES.SELECT_CLIP_UPLOAD);
    } else {
      this.selectType(null, this.MODAL_TYPES.ADD_PLAY);
    }
    this.showForm = true;

    this.store.dispatch(new fromStore.GetLibraryClips());
    this.setUserClips();
  }

  ngOnDestroy() {
    this.componentDestroyed.next();
    this.componentDestroyed.complete();
  }

  getGameDescription(game: any) {
    if (game && game.teams) {
      return game.teams.map((team) => team.name).join(' vs ');
    } else {
      return null;
    }
  }

  close() {
    this.closeModal.emit({});
  }

  save(addAnother = false) {
    if (this.viewType === this.MODAL_TYPES.SELECT_CLIP || this.viewType === this.MODAL_TYPES.SELECT_CLIP_UPLOAD) {
      this.playInfoForm.get('eventId').clearValidators();
      this.playInfoForm.get('gameId').clearValidators();
      this.playInfoForm.get('eventId').updateValueAndValidity();
      this.playInfoForm.get('gameId').updateValueAndValidity();
    }
    if (this.playInfoForm.invalid) {
      this.formHelpersService.markFieldsAsDirty(this.playInfoForm);
      return;
    }

    const formValues = this.playInfoForm.value;
    if (formValues.hrDate) {
      formValues.hrDate = dayjs(formValues.hrDate).format('YYYY-MM-DD');
    }

    this.closeModal.emit({
      play: formValues,
      gameInfo: {
        eventName: formValues.eventName || formValues.clipEvent,
        name: formValues.clipName || formValues.gameName || formValues.title,
        thumbnail: formValues.thumbnail || this.thumbnail,
        date: formValues.date || formValues.hrDate,
      },
      index: this.playInfo.index,
      showNext: addAnother,
    });
  }

  selectType($event, newType: string, formReset = false) {
    if ($event) {
      $event.preventDefault();
    }

    this.viewType = newType;

    switch (newType) {
      case this.MODAL_TYPES.ADD_PLAY:
        this.playInfoForm.get('type').setValue(this.MODAL_TYPES.ADD_PLAY);
        this.playInfoForm.get('videoUrl').clearValidators();
        this.playInfoForm.get('videoUrl').setErrors(null);
        this.playInfoForm.get('clipId').clearValidators();
        this.playInfoForm.get('clipId').setErrors(null);
        this.playInfoForm.get('title').clearValidators();
        this.playInfoForm.get('title').setErrors(null);
        this.playInfoForm.get('hrDate').clearValidators();
        this.playInfoForm.get('hrDate').setErrors(null);
        this.playInfoForm.get('hrDate').reset();
        this.playInfoForm.get('timecode').setValidators(Validators.required);
        this.playInfoForm.get('timecode').updateValueAndValidity();
        this.playInfoForm.updateValueAndValidity({ emitEvent: true });
        if (formReset) {
          this.playInfoForm.reset();
        }
        break;

      case this.MODAL_TYPES.SELECT_CLIP:
        this.playInfoForm.get('type').setValue(this.MODAL_TYPES.SELECT_CLIP);
        this.playInfoForm.get('videoUrl').clearValidators();
        this.playInfoForm.get('videoUrl').setErrors(null);
        this.playInfoForm.get('videoUrl').reset();
        this.playInfoForm.get('clipId').setValidators(Validators.required);
        this.playInfoForm.get('clipId').updateValueAndValidity();
        this.playInfoForm.get('title').clearValidators();
        this.playInfoForm.get('title').setErrors(null);
        this.playInfoForm.get('title').reset();
        this.playInfoForm.get('hrDate').clearValidators();
        this.playInfoForm.get('hrDate').setErrors(null);
        this.playInfoForm.get('hrDate').reset();
        this.playInfoForm.get('timecode').clearValidators();
        this.playInfoForm.get('timecode').setErrors(null);
        this.playInfoForm.updateValueAndValidity({ emitEvent: true });
        if (formReset) {
          this.playInfoForm.reset();
        }
        this.playInfoForm.get('clipOption').setValue(1);
        break;
      case this.MODAL_TYPES.SELECT_CLIP_UPLOAD:
        this.playInfoForm.get('type').setValue(this.MODAL_TYPES.SELECT_CLIP_UPLOAD);
        this.playInfoForm.get('videoUrl').setValidators(Validators.required);
        this.playInfoForm.get('videoUrl').updateValueAndValidity();
        this.playInfoForm.get('clipId').clearValidators();
        this.playInfoForm.get('clipId').setErrors(null);
        this.playInfoForm.get('clipId').reset();
        this.playInfoForm.get('title').setValidators(Validators.required);
        this.playInfoForm.get('title').updateValueAndValidity();
        this.playInfoForm
          .get('hrDate')
          .setValidators([
            Validators.required,
            Validators.pattern(/^(((0)[0-9])|((1)[0-2]))(\/)([0-2][0-9]|(3)[0-1])(\/)\d{2}$/i),
          ]);
        this.playInfoForm.get('hrDate').updateValueAndValidity();
        this.playInfoForm.get('description').clearValidators();
        this.playInfoForm.get('description').setErrors(null);
        this.playInfoForm.get('timecode').clearValidators();
        this.playInfoForm.get('timecode').setErrors(null);
        this.playInfoForm.get('thumbnail').reset();
        this.playInfoForm.get('clip').reset();
        this.playInfoForm.updateValueAndValidity({ emitEvent: true });
        if (formReset) {
          this.playInfoForm.reset();
        }
        this.playInfoForm.get('clipOption').setValue(2);
        break;
    }
  }

  get thumbnail() {
    if (this.viewType !== this.MODAL_TYPES.SELECT_CLIP_UPLOAD) {
      if (this.playInfoForm && this.playInfoForm.value.thumbnail) {
        return this.playInfoForm.value.thumbnail;
      } else if (this.playInfo && this.playInfo.gameInfo && this.playInfo.gameInfo.thumbnail) {
        return this.playInfo.gameInfo.thumbnail;
      }
    }
    return undefined;
  }

  get video() {
    return this.playInfoForm.value.clip;
  }

  get videoUrl() {
    return this.playInfoForm.get('videoUrl').value;
  }

  onUploadClipChange(uploadClip: boolean) {
    if (uploadClip) {
      this.selectType(null, this.MODAL_TYPES.SELECT_CLIP_UPLOAD);
    } else {
      this.selectType(null, this.MODAL_TYPES.SELECT_CLIP);
    }
  }

  onDrop($event) {
    $event.preventDefault();
    this.fileDrag = false;
    this.fileChanged({ target: $event.dataTransfer });
  }

  onDragOver(event) {
    event.stopPropagation();
    event.preventDefault();
    this.fileDrag = true;
  }

  onDragLeave(event) {
    event.stopPropagation();
    event.preventDefault();
    this.fileDrag = false;
  }

  fileChanged(event) {
    if (!event.target.files) {
      return;
    }

    this.uploadingVideo = true;
    this.uploadProgress = 0;

    const file: File = event.target.files[0];
    const extension = file.name.slice(file.name.lastIndexOf('.') + 1);

    const newFilename = new Date().getTime() + `.${extension}`;

    const uploadLocation = '_hr_user_uploads';
    this.assetsService
      .uploadFileToBucket(
        `video_assets/${uploadLocation}/${this.purchaseId}/${newFilename}`,
        file,
        environment.bucketName
      )
      .pipe(
        tap((response) => {
          if (response.loaded && response.total) {
            this.uploadProgress = Math.ceil((100 * response.loaded) / response.total);
          }
        }),
        filter((response) => response.ok && response.type === HttpEventType.Response)
      )
      .subscribe((uploadResponse) => {
        this.playInfoForm
          .get('videoUrl')
          .setValue(environment.fastly.main + '/' + `${uploadLocation}/${this.purchaseId}/${newFilename}`);
        this.uploadingVideo = false;
      });
  }

  private setUserClips() {
    this.store
      .select(fromStore.getLibraryClipsState)
      .pipe(takeUntil(this.componentDestroyed))
      .subscribe((state) => {
        if (state.success && state.ids && state.ids.length) {
          this.userClips = (state.ids as (number | string)[]).reduce((acc, v: number | string) => {
            acc.push({
              ...state.entities[v],
              value: v,
            });
            return acc;
          }, []);
        }
      });
  }
}
